'use client';

import { useId, useState } from 'react';

import { BarChart3, GraduationCap, Plug } from 'lucide-react';

import styles from './website-analytics-demo.module.css';

/** Presentation data only; never loads customer conversations or analytics. */
export function WebsiteAnalyticsDemo() {
  const [tab, setTab] = useState(0);
  const id = useId();
  const tabs = ['Overview', 'Enrolments', 'Integrations'];
  const icons = [BarChart3, GraduationCap, Plug];
  const metrics =
    tab === 0
      ? [
          ['Credits Used', '1,250 / 25,000', '23,750 remaining'],
          ['Actions Triggered', '48', 'All integrations'],
          ['Conversations Started', '120', 'This month'],
          ['Active Sessions', '3', 'Live sessions'],
        ]
      : [
          ['Total Attempts', '40', 'Enrolment flows started'],
          ['Successful', '30', '75% conversion · 24 confirmed'],
          ['Failed / Abandoned', '8', '2 waitlisted'],
          ['Revenue Invoiced', '$7,200', '24 paid · 6 awaiting payment'],
        ];
  return (
    <div className={styles.panel}>
      <header>
        <div>
          <h3>Sarah Analytics</h3>
          <p>Usage metrics and performance data</p>
        </div>
        <small>This month</small>
      </header>
      <div className={styles.tabs} role="tablist" aria-label="Analytics views">
        {tabs.map((name, i) => {
          const Icon = icons[i]!;
          return (
            <button
              type="button"
              key={name}
              id={`${id}-tab-${i}`}
              role="tab"
              aria-selected={tab === i}
              aria-controls={`${id}-panel`}
              onClick={() => setTab(i)}
            >
              <Icon size={15} />
              {name}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={`${id}-panel`}
        aria-labelledby={`${id}-tab-${tab}`}
        key={tab}
        className={styles.content}
      >
        {tab < 2 ? (
          <>
            <div className={styles.metrics}>
              {metrics.map(([name, value, note]) => (
                <div key={name}>
                  <span>{name}</span>
                  <b>{value}</b>
                  <small>{note}</small>
                </div>
              ))}
            </div>
            {tab === 0 ? (
              <>
                <section>
                  <h4>Estimated employee-hours saved</h4>
                  <p>≈ 1.5 hours this month · Based on agent credit usage</p>
                </section>
                <section>
                  <h4>Usage Trend</h4>
                  <p>Messages · Delivered and blocked</p>
                  <div
                    className={styles.chart}
                    aria-label="Weekly message volumes: 42, 68, 55, 91"
                  >
                    <div style={{ height: '42%' }}>42</div>
                    <div style={{ height: '68%' }}>68</div>
                    <div style={{ height: '55%' }}>55</div>
                    <div style={{ height: '91%' }}>91</div>
                  </div>
                  <div className={styles.axis}>
                    <span>Week 1</span>
                    <span>Week 2</span>
                    <span>Week 3</span>
                    <span>Week 4</span>
                  </div>
                </section>
                <section>
                  <h4>Communications Log</h4>
                  <p>Messages · Actions · Conversations</p>
                  <div className={styles.log}>
                    <span>Alex Morgan</span>
                    <span>I need help paying my invoice.</span>
                    <span>Jamie Lee</span>
                    <span>Can I update my booking?</span>
                  </div>
                </section>
              </>
            ) : (
              <>
                <section>
                  <h4>Conversion Funnel</h4>
                  {[
                    ['Started', 40],
                    ['Created', 30],
                    ['Confirmed', 24],
                  ].map(([label, n]) => (
                    <div className={styles.funnel} key={label}>
                      <span>{label}</span>
                      <i style={{ width: `${(Number(n) / 40) * 65}%` }} />
                      <b>{n}</b>
                    </div>
                  ))}
                </section>
                <div className={styles.metrics}>
                  <section>
                    <h4>Revenue</h4>
                    <b>$7,200</b>
                    <p>Invoiced amounts from enrolments</p>
                  </section>
                  <section>
                    <h4>Waitlist</h4>
                    <b>2</b>
                    <p>Waiting for a course place</p>
                  </section>
                </div>
                <section>
                  <h4>Contact Status</h4>
                  <p>Follow enrolment activity and filter by status.</p>
                </section>
              </>
            )}
          </>
        ) : (
          <section>
            <h4>Integrations</h4>
            <p>See your connected integrations in one place.</p>
            <div className={styles.integration}>
              <Plug size={28} />
              <span>Next integration will appear here</span>
            </div>
            <small>Connect an integration to see it here.</small>
          </section>
        )}
      </div>
    </div>
  );
}
